import React, { useCallback, useRef, useState } from 'react';
import { Box, Flex } from 'rebass';
import Icon from '../Icon/Icon';
import useSize from '@react-hook/size';

interface AccordionProps {
  children?: React.ReactNode;
  title?: string;
  icon?: JSX.Element;
  variant?: 'onPane' | 'onMain'; // not first class variant usage
}

const Accordion = ({
  children,
  title,
  icon,
  variant = 'onPane',
}: AccordionProps): JSX.Element => {
  // Handle open/close
  const [expand, setExpand] = useState(true);
  // Ref
  const contentRef = useRef<HTMLElement>(null);
  const [width, height] = useSize(contentRef);

  const onClick = useCallback(
    (e: any) => {
      if (!e.target.matches('button, input, a, button *, a *')) {
        setExpand(!expand);
      }
    },
    [expand]
  );

  return (
    <Box mt={4} mb={2} mx={variant === 'onPane' ? 20 : 0}>
      {/* HEADER */}
      <Flex justifyContent="flex-start" alignItems="center" pb={1}>
        <Flex
          flexGrow={1}
          alignItems="center"
          sx={{
            gap: 1,
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          onClick={onClick}
        >
          <Box
            sx={{
              flexShrink: 1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {title}
          </Box>
          <Icon
            icon="triangle-fill"
            size="7"
            style={{
              flexShrink: 0,
              transition: 'transform .3s ease',
              transform: expand ? `rotate(180deg)` : `rotate(90deg)`,
            }}
          />
        </Flex>
        {icon}
      </Flex>

      {/* BODY */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          transition: 'height .3s ease',
          height: expand ? (height ? height : 'auto') : 0,
        }}
      >
        <Box
          sx={{
            opacity: expand ? 1 : 0,
            transition: 'all .3s ease',
            transform: expand ? `translateY(0)` : `translateY(-100%)`,
          }}
          ref={contentRef}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Accordion;
